/**
 * stats/effectiveStats.ts — q-стата: эффективные статы юнита (D20/S2, tech/21 §7.1).
 *
 * `instance.stats` — БАЗА (перманент); непрерывные модификаторы — только Effect
 * со statMods-ДАННЫМИ. Эффективные значения считаются ПО ЗАПРОСУ:
 *   база → statMods всех активных эффектов, покрывающих юнит по scope-цепочке
 *   (unit / его cell / lane / table / player-владелец), слоями add → mul → set;
 *   тай-брейк внутри слоя — канонический порядок эффектов: scope-уровень
 *   table → lane → cell → player → unit, затем числовой effect.seq
 *   (tech/21 §7.1, tech/23 §5, D26/R13).
 *
 * health НЕ слоится (нет в StatKey): это текущее здоровье; его эффективное
 * значение — база с капом по эффективному maxHealth (tech/21 §7.1).
 * Снятие эффекта возвращает статы к базе автоматически — значение считается
 * на лету и никуда не «вшивается» (устранён layering-баг, R7).
 *
 * «Активные» эффекты в M2 — все в state.effects (тик duration и GC — M3b/M4).
 * Округления здесь нет: фиксированное floor — точка боя (tech/23 §5, шаг 5).
 */
import { parseCellId } from '../state/board.js';
import { requireInstance } from '../state/access.js';
import { normalizeZero } from '../state/num.js';
/** Ранг scope-уровня для канонического порядка эффектов (21 §7.1/23 §5). */
export const SCOPE_RANK = {
    table: 0,
    lane: 1,
    cell: 2,
    player: 3,
    unit: 4,
};
/**
 * Канонический порядок эффектов: по scope-уровню table→lane→cell→player→unit,
 * затем по ЧИСЛОВОМУ effect.seq (D26/R13 — число, не строка id).
 */
export function compareEffectsCanonical(a, b) {
    const byScope = SCOPE_RANK[a.scope.kind] - SCOPE_RANK[b.scope.kind];
    if (byScope !== 0)
        return byScope;
    return a.seq - b.seq;
}
/**
 * Активные эффекты, ПОКРЫВАЮЩИЕ юнит по его scope-цепочке (unit / cell / lane /
 * table / player-владелец), в КАНОНИЧЕСКОМ порядке эффектов (tech/21 §7.1/§9).
 * Используется q.stats (слои статов) и цепочкой модификаторов урона (tech/23 §5).
 */
export function coveringEffects(state, instanceId) {
    const inst = requireInstance(state, instanceId, 'coveringEffects');
    return state.effects.filter((e) => covers(e, inst)).sort(compareEffectsCanonical);
}
/** Покрывает ли эффект юнит по его scope-цепочке (tech/21 §7.1). */
function covers(effect, inst) {
    const scope = effect.scope;
    switch (scope.kind) {
        case 'unit':
            return scope.instanceId === inst.id;
        case 'player':
            return scope.playerId === inst.controllerId;
        case 'cell':
            return inst.cellId !== undefined && inst.cellId === scope.cellId;
        case 'lane': {
            if (inst.cellId === undefined)
                return false;
            const addr = parseCellId(inst.cellId);
            return addr.tableId === scope.tableId && addr.laneIndex === scope.laneIndex;
        }
        case 'table': {
            if (inst.cellId === undefined)
                return false;
            return parseCellId(inst.cellId).tableId === scope.tableId;
        }
    }
}
/** Фиксированный порядок слоёв (D20/S2): аддитивные → мультипликативные → set. */
const LAYERS = ['add', 'mul', 'set'];
/**
 * Эффективные статы юнита: база + statMods покрывающих эффектов (см. шапку).
 * Чистый запрос: состояние не мутируется.
 */
export function effectiveStats(state, instanceId) {
    const inst = requireInstance(state, instanceId, 'effectiveStats');
    const covering = coveringEffects(state, instanceId);
    const layered = { attack: inst.stats.attack, maxHealth: inst.stats.maxHealth };
    for (const layer of LAYERS) {
        for (const effect of covering) {
            if (effect.statMods === undefined)
                continue;
            for (const mod of effect.statMods) {
                if (mod.op !== layer)
                    continue;
                const cur = layered[mod.stat];
                layered[mod.stat] = normalizeZero(layer === 'add' ? cur + mod.value : layer === 'mul' ? cur * mod.value : mod.value);
            }
        }
    }
    const maxHealth = normalizeZero(layered.maxHealth);
    return {
        attack: normalizeZero(layered.attack),
        // health не слоится: база с капом по ЭФФЕКТИВНОМУ maxHealth (21 §7.1).
        health: normalizeZero(Math.min(inst.stats.health, maxHealth)),
        maxHealth,
    };
}
//# sourceMappingURL=effectiveStats.js.map